import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";
import {
    ArrowLeft,
    ArrowUpRight,
    BellRing,
    BookOpen,
    CalendarClock,
    Filter,
    Loader2,
    RefreshCw,
    Search as SearchIcon,
    Sparkles,
} from "lucide-react";

const STATUS_LABEL = {
    applied: "Applied",
    partial_application: "Partial application",
    in_force: "In force",
    proposed: "Proposed",
    repealed: "Repealed",
};

const STATUS_DOT = {
    applied: "bg-emerald-500",
    partial_application: "bg-amber-500",
    in_force: "bg-[#C9A84C]",
    proposed: "bg-sky-500",
    repealed: "bg-stone-400",
};

export default function RegulatoryIntelligence() {
    const { regId } = useParams();
    if (regId) return <RegulationDetail regId={regId} />;
    return <RegulationsList />;
}

function RegulationsList() {
    const [items, setItems] = useState([]);
    const [areas, setAreas] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("list");        // list | timeline | feed
    const [q, setQ] = useState("");
    const [areaFilter, setAreaFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [kindFilter, setKindFilter] = useState("");

    useEffect(() => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (areaFilter) params.set("area", areaFilter);
        if (statusFilter) params.set("status", statusFilter);
        if (kindFilter) params.set("kind", kindFilter);
        setLoading(true);
        api.get(`/regulations?${params.toString()}`)
            .then(({ data }) => {
                setItems(data.items || []);
                setAreas(data.areas || []);
                setCounts(data.counts_by_status || {});
            })
            .finally(() => setLoading(false));
    }, [q, areaFilter, statusFilter, kindFilter]);

    return (
        <div className="container-edit py-10 lg:py-14">
            <div className="label-eyebrow">Module</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                Regulatory Intelligence
            </h1>
            <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">
                Track 30+ EU regulations and directives: adoption, entry into force,
                phased application dates, and cross-regulation interplay. Refresh any
                instrument&apos;s live status from the detail page.
            </p>

            {/* View tabs */}
            <div className="mt-8 inline-flex border border-[#0D1B3E]/20 bg-white" data-testid="regulatory-view-tabs">
                {[
                    { key: "list", label: "Tracker" },
                    { key: "timeline", label: "Deadlines" },
                    { key: "feed", label: "Change feed" },
                ].map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setView(t.key)}
                        data-testid={`regulatory-view-${t.key}`}
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition-colors ${
                            view === t.key
                                ? "bg-[#0D1B3E] text-[#F5F0E8]"
                                : "text-[#0D1B3E]/80 hover:bg-[#0D1B3E]/5"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {view === "list" && (
                <>
                    <FiltersBar
                        q={q}
                        setQ={setQ}
                        area={areaFilter}
                        setArea={setAreaFilter}
                        areas={areas}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        kind={kindFilter}
                        setKind={setKindFilter}
                        counts={counts}
                    />
                    {loading ? (
                        <p className="mt-10 text-[#0D1B3E]/60 flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin" /> Loading regulations…
                        </p>
                    ) : items.length === 0 ? (
                        <p className="mt-10 text-[#0D1B3E]/60">No regulations match these filters.</p>
                    ) : (
                        <ul className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="regulatory-list">
                            {items.map((r) => (
                                <RegulationCard key={r.id} reg={r} />
                            ))}
                        </ul>
                    )}
                </>
            )}

            {view === "timeline" && <Timeline areaFilter={areaFilter} setArea={setAreaFilter} areas={areas} />}
            {view === "feed" && <ChangeFeed />}
        </div>
    );
}

function FiltersBar({ q, setQ, area, setArea, areas, statusFilter, setStatusFilter, kind, setKind, counts }) {
    return (
        <div className="mt-8 grid gap-3 lg:grid-cols-12">
            <div className="lg:col-span-5 relative">
                <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by title, CELEX, summary…"
                    className="field pl-9"
                    data-testid="regulatory-search-input"
                />
            </div>
            <select
                className="field lg:col-span-3"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                data-testid="regulatory-area-filter"
            >
                <option value="">All practice areas</option>
                {areas.map((a) => (
                    <option key={a.key} value={a.key}>
                        {a.name}
                    </option>
                ))}
            </select>
            <select
                className="field lg:col-span-2"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="regulatory-status-filter"
            >
                <option value="">All statuses</option>
                {Object.keys(STATUS_LABEL).map((k) => (
                    <option key={k} value={k}>
                        {STATUS_LABEL[k]}
                        {counts[k] ? ` (${counts[k]})` : ""}
                    </option>
                ))}
            </select>
            <select
                className="field lg:col-span-2"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                data-testid="regulatory-kind-filter"
            >
                <option value="">Regulations + Directives</option>
                <option value="regulation">Regulations only</option>
                <option value="directive">Directives only</option>
            </select>
        </div>
    );
}

function RegulationCard({ reg }) {
    const navigate = useNavigate();
    const open = () => navigate(`/app/regulatory/${reg.id}`);
    return (
        <li
            className="card-flat p-6 flex flex-col gap-3 cursor-pointer transition-colors hover:border-[#C9A84C] hover:bg-[#FFFDF7]"
            data-testid={`regulatory-card-${reg.id}`}
            onClick={open}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
            role="link"
            tabIndex={0}
            aria-label={`Open ${reg.short_title} details`}
        >
            <div className="flex items-center justify-between gap-3">
                <span
                    className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C9A84C]"
                    data-testid={`regulatory-card-kind-${reg.id}`}
                >
                    {reg.kind}
                </span>
                <StatusPill status={reg.status} />
            </div>
            <h3 className="font-display text-xl tracking-tight leading-snug">
                <Link
                    to={`/app/regulatory/${reg.id}`}
                    className="hover:text-[#C9A84C]"
                    data-testid={`regulatory-link-${reg.id}`}
                >
                    {reg.short_title}
                </Link>
            </h3>
            <p className="text-sm text-[#0D1B3E]/75 leading-relaxed line-clamp-3">
                {reg.summary}
            </p>
            <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-[#0D1B3E]/70">
                <Meta label="CELEX" value={reg.celex} mono />
                <Meta label="OJ" value={reg.oj_reference} />
                <Meta label="Adopted" value={reg.adopted_at} />
                <Meta label="Applies" value={reg.application_date} />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
                {(reg.practice_areas || []).map((a) => (
                    <span
                        key={a}
                        className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#0D1B3E]/15 text-[#0D1B3E]/70"
                    >
                        {a}
                    </span>
                ))}
            </div>
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#C9A84C]">
                View full profile <ArrowUpRight size={12} />
            </span>
        </li>
    );
}

function Meta({ label, value, mono }) {
    if (!value) return null;
    return (
        <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-[9px] uppercase tracking-[0.22em] text-[#0D1B3E]/45 shrink-0">
                {label}
            </span>
            <span className={`truncate ${mono ? "font-mono" : ""}`}>{value}</span>
        </div>
    );
}

function StatusPill({ status }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/75"
            data-testid={`regulatory-status-${status}`}
        >
            <span className={`inline-block w-1.5 h-1.5 ${STATUS_DOT[status] || "bg-stone-400"}`} />
            {STATUS_LABEL[status] || status}
        </span>
    );
}

function Timeline({ areaFilter, setArea, areas }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const params = new URLSearchParams();
        if (areaFilter) params.set("area", areaFilter);
        setLoading(true);
        api.get(`/regulations/timeline?${params.toString()}`)
            .then(({ data }) => setItems(data.items || []))
            .finally(() => setLoading(false));
    }, [areaFilter]);

    const today = new Date().toISOString().slice(0, 10);
    const upcoming = items.filter((i) => i.date >= today);
    const past = items.filter((i) => i.date < today).reverse();

    return (
        <div className="mt-8" data-testid="regulatory-timeline">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="label-eyebrow">Filter</span>
                <select
                    className="field max-w-xs"
                    value={areaFilter}
                    onChange={(e) => setArea(e.target.value)}
                    data-testid="regulatory-timeline-area-filter"
                >
                    <option value="">All practice areas</option>
                    {areas.map((a) => (
                        <option key={a.key} value={a.key}>{a.name}</option>
                    ))}
                </select>
            </div>
            {loading ? (
                <p className="mt-8 text-[#0D1B3E]/60 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading deadlines…
                </p>
            ) : (
                <>
                    <h2 className="font-display text-2xl mt-10 tracking-tight">Upcoming</h2>
                    {upcoming.length === 0 ? (
                        <p className="mt-3 text-sm text-[#0D1B3E]/60">No upcoming deadlines.</p>
                    ) : (
                        <ol className="mt-4 relative border-l border-[#0D1B3E]/15 pl-6 space-y-5">
                            {upcoming.map((d, i) => <TimelineRow key={i} d={d} upcoming />)}
                        </ol>
                    )}

                    <h2 className="font-display text-2xl mt-12 tracking-tight">Past</h2>
                    {past.length === 0 ? (
                        <p className="mt-3 text-sm text-[#0D1B3E]/60">No past deadlines on file.</p>
                    ) : (
                        <ol className="mt-4 relative border-l border-[#0D1B3E]/10 pl-6 space-y-4">
                            {past.map((d, i) => <TimelineRow key={i} d={d} />)}
                        </ol>
                    )}
                </>
            )}
        </div>
    );
}

function TimelineRow({ d, upcoming }) {
    return (
        <li className="relative" data-testid={`regulatory-timeline-row-${d.short_title}-${d.date}`}>
            <span
                className={`absolute -left-[31px] top-1 w-3 h-3 ${
                    upcoming ? "bg-[#C9A84C]" : "bg-[#0D1B3E]/30"
                }`}
            />
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A84C]">{d.date}</div>
            <div className="font-display text-lg leading-snug">
                <Link to={`/app/regulatory/${d.regulation_id}`} className="hover:text-[#C9A84C]">
                    {d.short_title} · {d.label}
                </Link>
            </div>
            <div className="text-xs text-[#0D1B3E]/65">{d.full_title}</div>
        </li>
    );
}

function ChangeFeed() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/regulations/feed")
            .then(({ data }) => setItems(data.items || []))
            .finally(() => setLoading(false));
    }, []);
    if (loading) {
        return (
            <p className="mt-10 text-[#0D1B3E]/60 flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Loading feed…
            </p>
        );
    }
    return (
        <ul className="mt-8 space-y-5" data-testid="regulatory-feed">
            {items.map((r) => (
                <li key={r.id} className="border-l-2 border-[#C9A84C] pl-5 py-1">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A84C]">
                        Entered into force {r.entry_into_force}
                    </div>
                    <Link
                        to={`/app/regulatory/${r.id}`}
                        className="font-display text-xl tracking-tight hover:text-[#C9A84C]"
                    >
                        {r.short_title} · {r.full_title}
                    </Link>
                    <p className="mt-1 text-sm text-[#0D1B3E]/75 line-clamp-2">{r.summary}</p>
                </li>
            ))}
        </ul>
    );
}

function RegulationDetail({ regId }) {
    const navigate = useNavigate();
    const [reg, setReg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        setLoading(true);
        api.get(`/regulations/${regId}`)
            .then(({ data }) => setReg(data))
            .catch(() => setErr("Could not load this regulation."))
            .finally(() => setLoading(false));
    }, [regId]);

    const refresh = async () => {
        if (!reg) return;
        setRefreshing(true);
        setErr("");
        try {
            const { data } = await api.post(`/regulations/${reg.id}/live-status`);
            setReg({ ...reg, live_status: data.live_status, live_status_updated_at: data.updated_at });
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : d?.message || "Could not refresh live status.");
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <div className="container-edit py-10">
                <p className="text-[#0D1B3E]/60 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading…
                </p>
            </div>
        );
    }
    if (!reg) {
        return (
            <div className="container-edit py-10">
                <p className="text-[#0D1B3E]/70">{err || "Not found."}</p>
                <Link to="/app/regulatory" className="btn-secondary mt-6 inline-flex">
                    <ArrowLeft size={14} /> Back
                </Link>
            </div>
        );
    }

    const ls = reg.live_status;

    return (
        <div className="container-edit py-10 lg:py-14 max-w-5xl" data-testid="regulatory-detail">
            <button
                type="button"
                onClick={() => navigate("/app/regulatory")}
                className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1"
                data-testid="regulatory-detail-back"
            >
                <ArrowLeft size={14} /> Back to tracker
            </button>

            <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C9A84C]">
                            {reg.kind}
                        </span>
                        <StatusPill status={reg.status} />
                    </div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-3 tracking-tight" data-testid="regulatory-detail-title">
                        {reg.short_title}
                    </h1>
                    <p className="mt-2 text-[#0D1B3E]/80">{reg.full_title}</p>
                </div>
                <a
                    href={reg.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    data-testid="regulatory-detail-eurlex"
                >
                    Open on EUR-Lex <ArrowUpRight size={14} />
                </a>
            </div>

            <p className="mt-7 text-base leading-relaxed text-[#0D1B3E]/85 max-w-3xl">
                {reg.summary}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <FactCard label="CELEX" value={reg.celex} mono />
                <FactCard label="Adopted" value={reg.adopted_at} />
                <FactCard label="Entry into force" value={reg.entry_into_force} />
                <FactCard label="Application" value={reg.application_date} />
            </div>

            {/* Tabs */}
            <div
                className="mt-10 inline-flex border border-[#0D1B3E]/20 bg-white flex-wrap"
                data-testid="regulatory-detail-tabs"
            >
                {[
                    { key: "overview", label: "Overview" },
                    { key: "provisions", label: "Provisions & obligations" },
                    { key: "impact", label: "Impact analysis" },
                    { key: "deadlines", label: "Deadlines" },
                    { key: "related", label: "Related instruments" },
                ].map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        data-testid={`regulatory-tab-${t.key}`}
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] min-h-[44px] transition-colors ${
                            tab === t.key
                                ? "bg-[#0D1B3E] text-[#F5F0E8]"
                                : "text-[#0D1B3E]/80 hover:bg-[#0D1B3E]/5"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <OverviewPanel
                    reg={reg}
                    ls={ls}
                    err={err}
                    refresh={refresh}
                    refreshing={refreshing}
                />
            )}
            {tab === "provisions" && <ProvisionsPanel reg={reg} />}
            {tab === "impact" && <ImpactPanel reg={reg} />}
            {tab === "deadlines" && <DeadlinesPanel reg={reg} />}
            {tab === "related" && <RelatedPanel reg={reg} />}

            <div className="mt-14 flex flex-wrap gap-3">
                <Link to="/app/research" className="btn-primary">
                    <BookOpen size={14} /> Open in Research Assistant
                </Link>
                <Link to="/app/watchdogs" className="btn-secondary">
                    <BellRing size={14} /> Create a watch
                </Link>
            </div>
        </div>
    );
}

function ProvisionsPanel({ reg }) {
    const provisions = reg.key_provisions || [];
    const obligations = reg.obligations || [];
    if (!provisions.length && !obligations.length) {
        return (
            <p className="mt-10 text-[#0D1B3E]/65" data-testid="regulatory-provisions-empty">
                Curated provisions are not yet available for this instrument. Open the
                full text on EUR-Lex above, or use the Impact analysis tab.
            </p>
        );
    }
    return (
        <div className="mt-10 grid gap-8 lg:grid-cols-2" data-testid="regulatory-provisions">
            <section>
                <div className="label-eyebrow">Key provisions</div>
                <h2 className="font-display text-2xl mt-2 tracking-tight">
                    What the instrument regulates
                </h2>
                <ul className="mt-5 space-y-3">
                    {provisions.map((p, i) => (
                        <li key={i} className="card-flat p-4 text-sm leading-relaxed flex gap-3">
                            <span className="font-display text-[#C9A84C] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                            <span className="text-[#0D1B3E]/85">{p}</span>
                        </li>
                    ))}
                </ul>
            </section>
            <section>
                <div className="label-eyebrow">Core obligations</div>
                <h2 className="font-display text-2xl mt-2 tracking-tight">
                    What in-scope organisations must do
                </h2>
                <ul className="mt-5 space-y-3">
                    {obligations.map((o, i) => (
                        <li key={i} className="card-flat p-4 text-sm leading-relaxed flex gap-3 border-l-2 border-l-[#C9A84C]">
                            <span className="text-[#0D1B3E]/85">{o}</span>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}

function OverviewPanel({ reg, ls, err, refresh, refreshing }) {
    return (
        <>
            {/* Live status */}
            <section className="mt-10">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="label-eyebrow">Live status</div>
                        <h2 className="font-display text-2xl mt-2 tracking-tight">
                            Where this instrument stands today
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={refresh}
                        disabled={refreshing}
                        className="btn-primary disabled:opacity-60 min-h-[44px]"
                        data-testid="regulatory-detail-refresh"
                    >
                        {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        {refreshing ? "Refreshing…" : ls ? "Refresh" : "Generate live status (1 credit)"}
                    </button>
                </div>
                {err && (
                    <p className="mt-3 text-sm text-red-700" data-testid="regulatory-detail-error">{err}</p>
                )}
                {ls ? (
                    <div className="mt-5 card-flat p-6" data-testid="regulatory-detail-livestatus">
                        <p className="text-base leading-relaxed">{ls.status_summary}</p>
                        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-[#0D1B3E]/55">
                            <span>Stage: {ls.stage || "–"}</span>
                            <span>Confidence: {ls.confidence}</span>
                            {ls.as_of && <span>As of: {ls.as_of}</span>}
                            {reg.live_status_updated_at && (
                                <span>Updated: {new Date(reg.live_status_updated_at).toLocaleString()}</span>
                            )}
                        </div>
                        {ls.recent_developments?.length > 0 && (
                            <Block title="Recent developments" items={ls.recent_developments} />
                        )}
                        {ls.implementing_acts?.length > 0 && (
                            <Block title="Implementing acts" items={ls.implementing_acts} />
                        )}
                        {ls.next_milestones?.length > 0 && (
                            <div className="mt-5">
                                <div className="label-eyebrow mb-2">Next milestones</div>
                                <ul className="space-y-1.5">
                                    {ls.next_milestones.map((m, i) => (
                                        <li key={i} className="text-sm flex items-baseline gap-3">
                                            <span className="text-[#C9A84C] font-mono text-xs">{m.date}</span>
                                            <span>{m.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {ls.open_questions?.length > 0 && (
                            <Block title="Open questions" items={ls.open_questions} />
                        )}
                    </div>
                ) : (
                    <p className="mt-5 text-sm text-[#0D1B3E]/60">
                        Click Refresh to generate a structured, source-grounded status update. Costs 1 credit.
                    </p>
                )}
            </section>
        </>
    );
}

function DeadlinesPanel({ reg }) {
    if (!reg.key_deadlines?.length) {
        return <p className="mt-8 text-sm text-[#0D1B3E]/60">No key deadlines recorded for this instrument.</p>;
    }
    return (
        <section className="mt-8">
            <div className="label-eyebrow">
                <CalendarClock size={11} className="inline -mt-0.5 mr-1" />
                Compliance deadlines
            </div>
            <ol className="mt-5 relative border-l border-[#0D1B3E]/15 pl-6 space-y-4">
                {reg.key_deadlines.map((d, i) => (
                    <li key={i} className="relative">
                        <span className="absolute -left-[31px] top-1 w-3 h-3 bg-[#C9A84C]" />
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A84C]">{d.date}</div>
                        <div className="font-display text-lg leading-snug">{d.label}</div>
                    </li>
                ))}
            </ol>
        </section>
    );
}

function RelatedPanel({ reg }) {
    if (!reg.related?.length) {
        return <p className="mt-8 text-sm text-[#0D1B3E]/60">No cross-regulation mappings recorded yet.</p>;
    }
    return (
        <section className="mt-8">
            <div className="label-eyebrow">
                <Sparkles size={11} className="inline -mt-0.5 mr-1" />
                Cross-regulation mapping
            </div>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {reg.related.map((r) => (
                    <li key={r.id} className="card-flat p-5">
                        <Link to={`/app/regulatory/${r.id}`} className="font-display text-lg hover:text-[#C9A84C]">
                            {r.short_title}
                        </Link>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                            {r.celex}
                        </div>
                        <p className="mt-2 text-sm text-[#0D1B3E]/80">{r.relation}</p>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function ImpactPanel({ reg }) {
    const [sectors, setSectors] = useState([]);
    const [sector, setSector] = useState("");
    const [orgType, setOrgType] = useState("");
    const [scale, setScale] = useState("");
    const [euOps, setEuOps] = useState(true);
    const [impact, setImpact] = useState(null);
    const [running, setRunning] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/regulations/impact/sectors")
            .then(({ data }) => setSectors(data.items || []))
            .catch(() => {});
    }, []);

    async function analyse(e) {
        e?.preventDefault?.();
        if (!sector) return;
        setRunning(true);
        setError("");
        try {
            const { data } = await api.post(`/regulations/${reg.id}/impact`, {
                sector,
                organisation_type: orgType || "",
                scale: scale || "",
                eu_operations: euOps,
            });
            // Async job pattern, poll until ready/failed
            const jobId = data.job_id;
            let done = null;
            for (let i = 0; i < 60; i += 1) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data: job } = await api.get(`/regulations/impact/jobs/${jobId}`);
                if (job.status === "ready") {
                    done = job.impact;
                    break;
                }
                if (job.status === "failed") {
                    throw new Error(job.error || "Impact analysis failed");
                }
            }
            if (!done) throw new Error("Timed out waiting for analysis. Please retry.");
            setImpact(done);
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setError(
                typeof d === "string"
                    ? d
                    : d?.message ||
                          e2?.message ||
                          "Impact analysis failed. Please retry.",
            );
        } finally {
            setRunning(false);
        }
    }

    async function exportDocx() {
        if (!impact) return;
        try {
            setExporting(true);
            const res = await api.post(
                `/exports/impact/${reg.id}.docx`,
                { sector, impact },
                { responseType: "blob" },
            );
            const blob = new Blob([res.data]);
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${reg.short_title}-${sector}-impact.docx`.replace(/[^A-Za-z0-9.-]/g, "-");
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                URL.revokeObjectURL(link.href);
                link.remove();
            }, 500);
        } catch (e2) {
            const d = e2?.response?.data;
            let msg = "Export failed";
            if (typeof d === "string") msg = d;
            else if (d?.detail) msg = typeof d.detail === "string" ? d.detail : (d.detail.message || msg);
            setError(msg);
        } finally {
            setExporting(false);
        }
    }

    return (
        <section className="mt-8" data-testid="regulatory-impact-panel">
            <div className="label-eyebrow">Impact analysis</div>
            <h2 className="font-display text-2xl mt-2 tracking-tight">
                Who is affected, how, and what to do
            </h2>
            <p className="mt-2 text-sm text-[#0D1B3E]/70 max-w-2xl">
                Sector-aware analysis grounded in the primary instrument text. Choose
                a sector, add optional organisation context, and receive obligations,
                deadlines and recommended actions you can export to Word.
            </p>

            <form onSubmit={analyse} className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <label className="label-eyebrow">Sector</label>
                    <select
                        className="field mt-2 min-h-[44px]"
                        value={sector}
                        onChange={(e) => setSector(e.target.value)}
                        required
                        data-testid="regulatory-impact-sector"
                    >
                        <option value="">Select sector…</option>
                        {sectors.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow">Organisation type</label>
                    <input
                        type="text"
                        className="field mt-2 min-h-[44px]"
                        placeholder="e.g. SaaS vendor, EU credit institution"
                        value={orgType}
                        onChange={(e) => setOrgType(e.target.value)}
                        data-testid="regulatory-impact-orgtype"
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Scale</label>
                    <input
                        type="text"
                        className="field mt-2 min-h-[44px]"
                        placeholder="e.g. 50 FTE, €25m ARR"
                        value={scale}
                        onChange={(e) => setScale(e.target.value)}
                        data-testid="regulatory-impact-scale"
                    />
                </div>
                <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={euOps}
                            onChange={(e) => setEuOps(e.target.checked)}
                            data-testid="regulatory-impact-euops"
                        />
                        EU operations
                    </label>
                </div>
                <div className="md:col-span-2 lg:col-span-4 flex flex-wrap gap-3">
                    <button
                        type="submit"
                        className="btn-primary min-h-[44px]"
                        disabled={running || !sector}
                        data-testid="regulatory-impact-run"
                    >
                        {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {running ? "Analysing…" : "Analyse impact (2 credits)"}
                    </button>
                    {impact && (
                        <button
                            type="button"
                            onClick={exportDocx}
                            disabled={exporting}
                            className="btn-secondary min-h-[44px]"
                            data-testid="regulatory-impact-export"
                        >
                            {exporting ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
                            Export to Word
                        </button>
                    )}
                </div>
            </form>

            {error && (
                <p className="mt-4 text-sm text-red-700" data-testid="regulatory-impact-error">
                    {error}
                </p>
            )}

            {impact && (
                <div className="mt-8 card-flat p-6 space-y-6" data-testid="regulatory-impact-result">
                    <div>
                        <div className="label-eyebrow">Executive summary</div>
                        <p className="mt-3 leading-relaxed">{impact.executive_summary}</p>
                    </div>

                    {impact.who_is_affected?.length > 0 && (
                        <div>
                            <div className="label-eyebrow">Who is affected</div>
                            <ul className="mt-3 space-y-3">
                                {impact.who_is_affected.map((row, i) => (
                                    <li key={i} className="border-l-2 border-[#C9A84C] pl-4">
                                        <div className="font-display text-lg">{row.actor}</div>
                                        <div className="text-sm text-[#0D1B3E]/80">{row.why}</div>
                                        {row.threshold && (
                                            <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#0D1B3E]/55">
                                                Threshold: {row.threshold}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {impact.key_obligations?.length > 0 && (
                        <div>
                            <div className="label-eyebrow">Key obligations</div>
                            <ul className="mt-3 space-y-3">
                                {impact.key_obligations.map((o, i) => (
                                    <li key={i}>
                                        <div className="font-display text-base">
                                            [{(o.severity || "medium").toUpperCase()}] {o.obligation}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-[0.2em] text-[#0D1B3E]/55 mt-1">
                                            {o.source}
                                        </div>
                                        <p className="text-sm text-[#0D1B3E]/85 mt-1">{o.what_to_do}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {impact.deadlines?.length > 0 && (
                        <div>
                            <div className="label-eyebrow">Deadlines</div>
                            <ol className="mt-3 relative border-l border-[#0D1B3E]/15 pl-6 space-y-3">
                                {impact.deadlines.map((d, i) => (
                                    <li key={i} className="relative">
                                        <span className="absolute -left-[31px] top-1 w-3 h-3 bg-[#C9A84C]" />
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A84C]">
                                            {d.date}
                                        </div>
                                        <div className="font-display text-base">{d.label}</div>
                                        {d.action && (
                                            <p className="text-sm text-[#0D1B3E]/80 mt-1">{d.action}</p>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {impact.recommended_actions?.length > 0 && (
                        <div>
                            <div className="label-eyebrow">Recommended actions</div>
                            <ul className="mt-3 space-y-2">
                                {impact.recommended_actions.map((a, i) => (
                                    <li key={i} className="text-sm">
                                        <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] mr-2">
                                            {a.priority}
                                        </span>
                                        {a.action}
                                        {a.owner && (
                                            <span className="text-[#0D1B3E]/60"> · {a.owner}</span>
                                        )}
                                        {a.timeframe && (
                                            <span className="text-[#0D1B3E]/60"> · {a.timeframe}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {impact.primary_sources?.length > 0 && (
                        <div>
                            <div className="label-eyebrow">Primary sources</div>
                            <ul className="mt-3 space-y-1 text-sm">
                                {impact.primary_sources.map((s, i) => (
                                    <li key={i}>
                                        {s.url ? (
                                            <a
                                                href={s.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="hover:text-[#C9A84C]"
                                            >
                                                {s.label}
                                            </a>
                                        ) : (
                                            <span>{s.label}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {impact.caveats?.length > 0 && (
                        <div className="text-xs text-[#0D1B3E]/60">
                            {impact.caveats.join(" · ")}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

function FactCard({ label, value, mono }) {
    return (
        <div className="card-flat p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">{label}</div>
            <div className={`mt-1 text-sm ${mono ? "font-mono" : ""}`}>{value || "–"}</div>
        </div>
    );
}

function Block({ title, items }) {
    return (
        <div className="mt-5">
            <div className="label-eyebrow mb-2">{title}</div>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#0D1B3E]/85">
                {items.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
        </div>
    );
}
